/** FullCalendar CSS overrides — Tailwind-aligned tokens (no MUI sx usage needed for the calendar container itself) */

/** Type-based colors for calendar events — #17 */
export const typeColors: Record<number, string> = {
  1: "#D4572B", // Reactive — terracotta
  2: "#3B82F6", // Preventive — blue
  3: "#8C6BD4", // Predictive — purple
  4: "#2D8C4E", // Inspection — green
  5: "#6B7280", // GeneralTask — gray
};

/** Status-based background opacity */
export const statusOpacity: Record<number, number> = {
  1: 0.6,  // Created
  2: 0.6,  // Assigned
  3: 1.0,  // Scheduled
  4: 1.0,  // InProgress
  5: 0.5,  // OnHold
  6: 0.3,  // Completed
  7: 0.3,  // Verified
  8: 0.2,  // Cancelled
};

/** Legend items for the type color bar — 5 items only (#14), updated colors (#17) */
export const legendItems = [
  { typeId: 1, labelKey: "types.reactive", color: "#D4572B", label: "Reactive" },
  { typeId: 2, labelKey: "types.preventiveMaintenance", color: "#3B82F6", label: "Preventive" },
  { typeId: 3, labelKey: "types.predictiveMaintenance", color: "#8C6BD4", label: "Predictive" },
  { typeId: 4, labelKey: "types.inspection", color: "#2D8C4E", label: "Inspection" },
  { typeId: 5, labelKey: "types.generalTask", color: "#6B7280", label: "General" },
];

/**
 * FullCalendar CSS overrides — injected as a <style> tag inside the calendar container.
 * #18 - no box-shadow, border/radius handled by outer Tailwind container
 * #19 - column headers: Sora 11px/400 uppercase #8C9099 for day names, Sora 20px/600 #0C1117 for date numbers
 * #20 - today highlight: terracotta tint, terracotta circle on day number
 * #21 - time labels: Mono 10px #8C9099
 * #22 - grid lines: #E8E5DF
 * #23 - weekend tint: rgba(0,0,0,0.015)
 * #24 - now indicator: #D4572B (terracotta)
 * #27 - remove scrollgrid border/radius (outer container handles it)
 */
export const calendarCssOverrides = `
  /* Base font */
  .fc {
    font-family: 'Sora', sans-serif;
  }
  /* Hide built-in toolbar */
  .fc .fc-toolbar {
    display: none;
  }

  /* #27 — Remove scrollgrid border/radius — outer container handles it */
  .fc .fc-scrollgrid {
    border: none !important;
    border-radius: 0 !important;
    overflow: visible;
  }

  /* #22 — Grid lines */
  .fc .fc-scrollgrid td,
  .fc .fc-scrollgrid th {
    border-color: #E8E5DF;
  }

  /* #19 — Column headers: day name = Sora 11px/400 uppercase #8C9099 */
  .fc .fc-col-header-cell {
    font-family: 'Sora', sans-serif;
    font-size: 11px;
    font-weight: 400;
    text-transform: uppercase;
    color: #8C9099;
    padding: 10px 0 4px 0;
    border-bottom: 1px solid #E8E5DF;
    background: transparent;
  }
  .fc .fc-col-header-cell-cushion {
    color: #8C9099;
    text-decoration: none;
    font-family: 'Sora', sans-serif;
    font-size: 11px;
    font-weight: 400;
    text-transform: uppercase;
  }

  /* #19 — Day numbers in header: Sora 20px/600 #0C1117 */
  .fc .fc-daygrid-day-number {
    font-family: 'Sora', sans-serif;
    font-size: 20px;
    font-weight: 600;
    color: #0C1117;
    text-decoration: none;
    padding: 6px 8px;
  }
  /* Week view day header numbers */
  .fc .fc-timegrid-axis,
  .fc .fc-col-header-cell a {
    text-decoration: none;
  }

  /* #20 — Today: terracotta tint on header + column */
  .fc .fc-day-today {
    background-color: rgba(212, 87, 43, 0.03) !important;
  }
  .fc .fc-col-header-cell.fc-day-today {
    background-color: rgba(212, 87, 43, 0.04) !important;
  }
  .fc .fc-col-header-cell.fc-day-today .fc-col-header-cell-cushion {
    color: #D4572B;
  }
  /* Today day number circle */
  .fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-number {
    color: #fff;
    background-color: #D4572B;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
  }

  /* #21 — Time labels: JetBrains Mono 10px #8C9099 */
  .fc .fc-timegrid-slot-label-cushion {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: #8C9099;
  }

  /* #23 — Weekend tint */
  .fc .fc-day-sat,
  .fc .fc-day-sun {
    background-color: rgba(0, 0, 0, 0.015);
  }

  /* #24 — Now indicator: terracotta */
  .fc .fc-timegrid-now-indicator-line {
    border-color: #D4572B;
    border-width: 2px;
  }
  .fc .fc-timegrid-now-indicator-arrow {
    border-color: #D4572B;
  }

  /* #25 — Event styling: white card with 3px left type-color border */
  .fc .fc-event {
    background-color: #FFFFFF !important;
    border: none !important;
    border-left: 3px solid var(--fc-event-border-color, #6B7280) !important;
    border-radius: 6px !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
    cursor: pointer;
    font-size: 12px;
    padding: 0;
    transition: box-shadow 0.2s ease, transform 0.15s ease;
    overflow: hidden;
  }
  .fc .fc-event:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    transform: translateY(-1px);
  }
  .fc .fc-timegrid-event .fc-event-main,
  .fc .fc-daygrid-event .fc-event-main {
    padding: 0;
    color: #0C1117 !important;
  }
  /* Month view: compact dot events keep the left border */
  .fc .fc-daygrid-event {
    margin: 1px 2px;
  }
  .fc .fc-timegrid-event {
    margin: 0 2px;
  }

  /* Drag styling */
  .fc .fc-event-dragging {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15) !important;
    transform: scale(1.02);
  }

  /* Resize handle — subtle bottom grab bar */
  .fc .fc-event .fc-event-resizer-end {
    height: 6px;
    bottom: 0;
    left: 0;
    right: 0;
    cursor: s-resize;
    background: transparent;
    transition: background 0.15s ease;
  }
  .fc .fc-event:hover .fc-event-resizer-end {
    background: rgba(0, 0, 0, 0.06);
  }
  .fc .fc-event .fc-event-resizer-end::after {
    content: '';
    position: absolute;
    bottom: 2px;
    left: 50%;
    transform: translateX(-50%);
    width: 20px;
    height: 2px;
    border-radius: 1px;
    background: rgba(0, 0, 0, 0.15);
    opacity: 0;
    transition: opacity 0.15s ease;
  }
  .fc .fc-event:hover .fc-event-resizer-end::after {
    opacity: 1;
  }
  /* Resize in progress styling */
  .fc .fc-event-resizing {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15) !important;
    z-index: 10;
  }

  /* Highlight */
  .fc .fc-highlight {
    background-color: rgba(52, 152, 219, 0.08);
  }

  /* Status opacity modifiers (kept for completed/cancelled dimming) */
  .fc-event-opacity-3 { opacity: 0.45; }
  .fc-event-opacity-5 { opacity: 0.6; }
  .fc-event-opacity-6 { opacity: 0.8; }

  /* Week number cell */
  .fc .fc-timegrid-axis-cushion,
  .fc .fc-daygrid-week-number {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: #8C9099;
  }

  /* All-day events in week/day views */
  .fc .fc-daygrid-event-harness {
    margin: 1px 2px;
  }
  .fc .fc-timegrid-col .fc-daygrid-event {
    border-left: 3px solid var(--fc-event-border-color, #6B7280) !important;
    border-radius: 6px !important;
  }
  /* All-day section header label */
  .fc .fc-timegrid-axis-frame {
    font-family: 'Sora', sans-serif;
    font-size: 10px;
    color: #8C9099;
  }

  /* ─── Phone responsive: touch-friendly event sizing ─── */
  @media (max-width: 768px) {
    .fc .fc-timegrid-event {
      min-height: 48px !important;
    }
    .fc .fc-timegrid-event .fc-event-main {
      min-height: 44px;
    }
    /* Larger touch target for resize handle on mobile */
    .fc .fc-event .fc-event-resizer-end {
      height: 12px;
    }
    /* Hide week numbers on phone — saves horizontal space */
    .fc .fc-timegrid-axis-cushion,
    .fc .fc-daygrid-week-number {
      display: none;
    }
    /* Prevent horizontal overflow */
    .fc .fc-scrollgrid {
      max-width: 100% !important;
    }
    .fc .fc-scroller {
      overflow-x: hidden !important;
    }
  }

  /* ─── Tablet responsive ─── */
  @media (min-width: 769px) and (max-width: 1200px) {
    /* Slightly smaller time labels to gain space */
    .fc .fc-timegrid-slot-label-cushion {
      font-size: 9px;
    }
  }
`;
